import 'react-notifications/lib/notifications.css';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import axios from 'axios';
import {
	Button,
	Typography,
	Card,
	CardActions,
	CardContent,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	Table,
	TableBody,
	TableRow,
	TableCell,
	CircularProgress,
} from '@material-ui/core';
import SettingsTable from '../settings/SettingsTable';
import UpdatePaymentForm from '../settings/UpdatePaymentForm';
import UpdateBillingEmailForm from '../settings/UpdateBillingEmailForm';
import Invoices from '../settings/Invoices';
import * as actions from '../../actions/settings';

const rightColumnStyle = {
	width: 240,
};

const leftColumnStyle = {
	width: 210,
};

const cardStyle = {
	width: '100%',
	marginTop: 24,
	marginBottom: 24,
	borderRadius: 12,
	boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
	overflow: 'hidden',
	backgroundColor: '#fff',
};

const cardHeaderStyle = {
	backgroundColor: '#f8fafc',
	padding: '24px 24px 20px',
	borderBottom: '1px solid #f3f4f6',
	display: 'flex',
	flexDirection: 'column',
};

const headerTitleStyle = {
	fontWeight: 400,
	fontSize: '14px',
	color: '#22223b',
	marginBottom: 4,
};

const headerSubtitleStyle = {
	fontWeight: 400,
	fontSize: '0.875rem',
	color: '#757575',
	marginTop: '-2px',
};

const billingCellStyle = {
	padding: '12px 24px',
	fontSize: '0.875rem',
	color: '#475569',
	borderBottom: '1px solid #f1f5f9',
	verticalAlign: 'middle',
};

const billingLabelStyle = {
	...billingCellStyle,
	color: '#64748b',
	fontWeight: 600,
	letterSpacing: '0.02em',
	textTransform: 'uppercase',
	fontSize: '0.6875rem',
};

const billingActionButtonStyle = {
	textTransform: 'none',
	fontWeight: 500,
	padding: '8px 16px',
	borderRadius: 10,
	boxShadow: 'none',
};

class SettingsDashboard extends Component {
	state = {
		open: false,
		dialog: '',
		billingEmail: '',
		currentPeriodStart: '',
		currentPeriodEnd: '',
		loadingBillingPeriod: false,
	};

	async componentDidMount() {
		document.title = 'Settings | Chekkit Enterprise';
		this.props.fetchPaymentDetails();
		this.getBillingPeriod();
		this.setBillingEmail();
	}

	getBillingPeriod = async () => {
		try {
			this.setState({ loadingBillingPeriod: true });
			const res = await axios.get('/api/billing/period');
			const { currentPeriodStart, currentPeriodEnd } = res.data;

			this.setState({
				currentPeriodStart,
				currentPeriodEnd,
				loadingBillingPeriod: false,
			});
		} catch (err) {
			this.setState({ loadingBillingPeriod: false });
		}
	};

	setBillingEmail = async () => {
		try {
			const res = await axios.get('/api/billing/email');

			if (res.data.billingEmail) {
				this.setState({ billingEmail: res.data.billingEmail });
			}
		} catch (err) {
			// non-fatal
		}
	};

	openDialog = (dialog) => {
		this.setState({
			open: true,
			dialog,
		});
	};

	closeDialog = () => {
		this.setState({ open: false });
	};

	renderPaymentDetails = () => (
		<div>
			{this.props.paymentDetails &&
				this.props.paymentDetails.paymentDetails &&
				this.props.paymentDetails.paymentDetails}
		</div>
	);

	renderBillingEmail = () => <span>{this.state.billingEmail}</span>;

	renderBillingPeriod = () => {
		const { currentPeriodStart, currentPeriodEnd, loadingBillingPeriod } =
			this.state;

		if (loadingBillingPeriod) {
			return <CircularProgress size={20} />;
		}

		if (currentPeriodStart && currentPeriodEnd) {
			return `${moment
				.unix(currentPeriodStart)
				.format('MMM DD')} to ${moment
				.unix(currentPeriodEnd)
				.format('MMM DD')}`;
		}

		return <span />;
	};

	renderDialogForm = () => {
		switch (this.state.dialog) {
			case 'payment':
				return <UpdatePaymentForm closeDialog={this.closeDialog} />;
			case 'email':
				return (
					<UpdateBillingEmailForm
						setBillingEmail={this.setBillingEmail}
						closeDialog={this.closeDialog}
					/>
				);
			case 'invoices':
				return <Invoices closeDialog={this.closeDialog} />;
			default:
				return <div />;
		}
	};

	renderDialogTitle = () => {
		switch (this.state.dialog) {
			case 'payment':
				return 'Update Payment Info';
			case 'email':
				return 'Update Billing Email';
			case 'invoices':
				return 'View Invoices';
			default:
				return '';
		}
	};

	renderDialog = () => (
		<Dialog
			maxWidth='sm'
			fullWidth
			open={this.state.open}
			onClose={this.closeDialog}
			disableRestoreFocus
		>
			<DialogTitle>{this.renderDialogTitle()}</DialogTitle>
			<DialogContent>{this.renderDialogForm()}</DialogContent>
		</Dialog>
	);

	renderBillingSettings = () => {
		if (this.props.auth._reseller) {
			return (
				<div>
					<Paper elevation={0} style={cardStyle}>
						<div style={cardHeaderStyle}>
							<Typography variant='h6' style={headerTitleStyle}>
								Billing
							</Typography>
							<Typography style={headerSubtitleStyle}>
								View and manage your billing settings.
							</Typography>
						</div>

						<Table size='small'>
							<TableBody>
								<TableRow>
									<TableCell
										style={{
											...billingLabelStyle,
											...leftColumnStyle,
										}}
									>
										Payment Method
									</TableCell>
									<TableCell style={billingCellStyle}>
										{this.renderPaymentDetails()}
									</TableCell>
									<TableCell
										style={{
											...billingCellStyle,
											...rightColumnStyle,
											textAlign: 'right',
											borderBottom: '1px solid #f1f5f9',
										}}
									>
										<Button
											variant='contained'
											color='secondary'
											disableElevation
											style={{
												...billingActionButtonStyle,
												backgroundColor: '#000000',
												color: '#fff',
											}}
											aria-label='Update credit card'
											title='Update credit card'
											onClick={() =>
												this.openDialog('payment')
											}
										>
											Update
										</Button>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell
										style={{
											...billingLabelStyle,
											...leftColumnStyle,
										}}
									>
										Billing Email
									</TableCell>
									<TableCell style={billingCellStyle}>
										{this.renderBillingEmail()}
									</TableCell>
									<TableCell
										style={{
											...billingCellStyle,
											...rightColumnStyle,
											textAlign: 'right',
										}}
									>
										<Button
											variant='contained'
											color='secondary'
											disableElevation
											style={{
												...billingActionButtonStyle,
												backgroundColor: '#000000',
												color: '#fff',
											}}
											aria-label='Update billing email'
											title='Update billing email'
											onClick={() =>
												this.openDialog('email')
											}
										>
											Update
										</Button>
									</TableCell>
								</TableRow>

								<TableRow>
									<TableCell
										style={{
											...billingLabelStyle,
											...leftColumnStyle,
										}}
									>
										Current Billing Period
									</TableCell>
									<TableCell style={billingCellStyle}>
										{this.renderBillingPeriod()}
									</TableCell>
									<TableCell
										style={{
											...billingCellStyle,
											...rightColumnStyle,
										}}
									/>
								</TableRow>

								<TableRow>
									<TableCell
										style={{
											...billingLabelStyle,
											...leftColumnStyle,
											borderBottom: 'none',
										}}
									>
										Invoices
									</TableCell>
									<TableCell
										style={{
											...billingCellStyle,
											borderBottom: 'none',
										}}
									>
										View and download invoices
									</TableCell>
									<TableCell
										style={{
											...billingCellStyle,
											...rightColumnStyle,
											textAlign: 'right',
											borderBottom: 'none',
										}}
									>
										<Button
											variant='contained'
											color='secondary'
											disableElevation
											style={{
												...billingActionButtonStyle,
												backgroundColor: '#000000',
												color: '#fff',
											}}
											aria-label='View invoices'
											title='View invoices'
											onClick={() =>
												this.openDialog('invoices')
											}
										>
											View
										</Button>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</Paper>
					{this.renderDialog()}
				</div>
			);
		}

		return <div />;
	};

	renderSelectLocationsTable = () =>
		this.props.auth && this.props.auth.googleRefresh ? (
			<SettingsTable compact={Boolean(this.props.auth._reseller)} />
		) : null;

	render() {
		if (this.props.auth) {
			return (
				<div>
					{this.renderBillingSettings()}
					{this.renderSelectLocationsTable()}
				</div>
			);
		}

		return (
			<Card
				zdepth={1}
				style={{
					maxWidth: '350px',
					margin: 'auto',
					marginTop: '8px',
				}}
			>
				<CardContent>
					<Typography>Please Sign in to Chekkit</Typography>
				</CardContent>
				<CardActions>
					<Button href='/api/logout'>Sign In</Button>
				</CardActions>
			</Card>
		);
	}
}

function mapStateToProps(state) {
	return {
		error: state.error,
		auth: state.auth,
		paymentDetails: state.paymentDetails,
	};
}

export default connect(mapStateToProps, actions)(SettingsDashboard);



// WEBPACK FOOTER //
// ./src/components/dashboards/SettingsDashboard.js